import React, {useState, useEffect, useLayoutEffect} from "react"
import _ from "lodash"
import Isvg from "react-inlinesvg"
import moment from "moment"
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { field as _field } from "components/table/lib/column-field"
import {sortFoldersFirst, sortCode, sortPath, sortDate, sortUser} from "components/folder/explorer/sort"
import { CommercialIcon, HospitalityIcon, MixedUseIcon, MFRentIcon } from 'images/icons/sidebar'
import { Folder as FolderIcon } from "images/icons/tools/"
import SquareSpinner from 'components/spinner/square-spinner'
import Tooltip from "components/folder/explorer/tooltip"
import folderService from "data/folder"

type IFolderSearch = {
  searchTerm: string,
  dblClick: Function
}

const FolderSearch = (props: IFolderSearch) => {
  const [results, setResults] = useState<object[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const field = _field('explorer')

  useEffect(() => {
    setLoading(true)
    setIsDataLoaded(false)
    const timer = setTimeout(() => {
      folderService.folderExplorerSearch(props.searchTerm)
        .then(res => {
          setResults(sortCode({order: 1}, res))
          setLoading(false)
          setIsDataLoaded(true)
        })
    }, 800)

    return () => {
      clearTimeout(timer)
    }
  }, [props.searchTerm])

  useLayoutEffect(() => {
    if(!isDataLoaded) return;
    const tooltippedElements = document.querySelectorAll('[data-tooltip]');
    if(!tooltippedElements.length) return

    const handleMouseMove = (event) => {
      const el = event.currentTarget;
      const tooltipText = el.getAttribute('data-tooltip');
      if (!tooltipText) return;

      el.classList.add('tooltip-visible');

      const tooltipRect = {
        width: el.offsetWidth,
        height: el.offsetHeight
      };

      const mouseX = event.clientX;
      const mouseY = event.clientY;

      // Position the tooltip near the mouse cursor
      const tooltipX = mouseX - el.offsetWidth / 2;
      const tooltipY = mouseY - el.offsetHeight - 10;

      el.style.setProperty('--tooltip-x', `${tooltipX}px`);
      el.style.setProperty('--tooltip-y', `${tooltipY}px`);
    };

    const handleMouseLeave = (event) => {
      const el = event.currentTarget;
      el.classList.remove('tooltip-visible');
    };

    tooltippedElements.forEach(el => {
      el.addEventListener('mousemove', handleMouseMove);
      el.addEventListener('mouseleave', handleMouseLeave);
    });

    // Cleanup event listeners on unmount
    return () => {
      tooltippedElements.forEach(el => {
        el.removeEventListener('mousemove', handleMouseMove);
        el.removeEventListener('mouseleave', handleMouseLeave);
      });
    };
  }, [isDataLoaded]);

  return (
    <div className="details-table">
      { loading && <SquareSpinner />}
      { !loading &&
        <DataTable
          id="folder-explorer-table"
          value={results}
          paginator={false}
          scrollable={true}
          onRowDoubleClick={e => props.dblClick(e)}
          stateStorage="custom"
          customSaveState={state => localStorage.setItem("folder-explorer-table-preferences", JSON.stringify({rows: state.rows}))}
          customRestoreState={() => JSON.parse(localStorage.getItem("folder-explorer-table-preferences")!)}>
          <Column header="Type" style={{width: "60px", textAlign: 'center'}}
            body={(rowData, _props) => _.get(rowData, "type") === "Commercial" ? <Isvg src={CommercialIcon} className="icon" /> :
                                        _.get(rowData, "type") === "Multifamily" ? <Isvg src={MFRentIcon} className="icon" /> :
                                        _.get(rowData, "type") === "Hospitality" ? <Isvg src={HospitalityIcon} className="icon" /> :
                                        _.get(rowData, "type") === "Mixed-Use" ? <Isvg src={MixedUseIcon} className="icon" /> :
                                        _.isNumber(_.get(rowData, "folderid")) ? <Isvg src={FolderIcon} className="icon" /> : null }
          />
          <Column style={{width: "240px", overflow: "hidden", textOverflow: "ellipsis"}}
            sortable
            header="Code"
            sortFunction={(e) => sortCode(e, results)}
            {...field('code', (rowData, props) => <span data-tooltip={rowData.code}>{rowData.code}</span>)}
          />
          <Column style={{overflow: "hidden", textOverflow: "ellipsis"}}
            sortable
            header="Path"
            sortFunction={(e) => sortPath(e, results)}
            {...field('path', (rowData, props) => <span data-tooltip={rowData.path}>{rowData.path}</span>)}
          />
          <Column style={{width: "150px"}}
            sortable
            header="Last Updated"
            sortFunction={(e) => sortDate(e, results)}
            {...field("activitydetail.lastupdated", (rowData) => _.get(rowData, "activitydetail.lastupdated") ? moment(_.get(rowData, "activitydetail.lastupdated")).format("MM/DD/YYYY hh:mm A") : "-")}
          />
          <Column style={{width: "150px"}}
            sortable
            header="Last Updated By"
            sortFunction={(e) => sortUser(e, results)}
            {...field("activitydetail.user", (rowData) => _.get(rowData, "activitydetail.user") ? _.get(rowData, "activitydetail.user") : "-")}
          />
        </DataTable>
      }
    </div>
  )
}

export default FolderSearch
